<template>
	<RequirePermission :permissions="[PermissionKey.CreateTaskRequest]">
		<ion-modal
			ref="modal"
			:is-open="showModal"
			@did-dismiss="closeGlobalModal"
			@will-present="onWillPresent"
		>
			<ion-header>
				<ion-toolbar color="branding">
					<ion-buttons slot="end">
						<ion-button color="medium" @click="dismiss">
							<ion-icon :icon="close" />
						</ion-button>
					</ion-buttons>
					<ion-title>Task Request</ion-title>
				</ion-toolbar>
			</ion-header>

			<div class="flex h-full flex-col gap-1 overflow-y-auto p-4">
				<PlantingSelectorTrigger v-model="selectedPlantingIds" />

				<div
					class="flex w-full flex-row items-center justify-between gap-2"
				>
					<div class="flex-1">
						<sg-select-modal
							v-model:single="selectedType"
							label="Type"
							modal-title="Select Task Type"
							:searchbar="false"
							:items="taskTypeItems"
							:unselectable="false"
						>
							<template #selectElementValue>
								<TaskTypeChip :type="selectedType" />
							</template>
						</sg-select-modal>
					</div>
					<div class="flex-1">
						<sg-datetime-button
							label="Date"
							datetime="task-request-date"
						/>
						<date-modal
							v-model="selectedDate"
							datetime-id="task-request-date"
							title="Choose Task Date"
						/>
					</div>
				</div>
				<sg-input
					v-if="selectedType === TaskType.General"
					v-model="taskTitle"
					label="Title"
				/>

				<!-- Show a warning if the task type doesn't match the planting method -->
				<PlantMethodConflictWarning
					class="mt-1"
					:selected-type="selectedType"
					:selected-planting-ids="selectedPlantingIds"
				/>
				<sg-textarea
					v-model="requestComments"
					label="Comments"
					:rows="6"
				/>

				<BonipakTaskRequestWeedFieldForm
					v-if="
						selectedType === TaskType.Weeding &&
						myCustomer?.id === 4
					"
					v-model="bonipakFormData"
				/>

				<BonipakTaskRequestThinFieldForm
					v-if="
						selectedType === TaskType.Thinning &&
						myCustomer?.id === 4
					"
					v-model="bonipakThinFormData"
					:selected-planting-id="selectedPlantingIds?.[0]"
				/>
			</div>

			<ion-footer>
				<ion-toolbar>
					<ion-button
						:disabled="!canSubmit || isSubmitting"
						color="primary"
						expand="block"
						@click="submit"
					>
						{{ isSubmitting ? "Submitting..." : "Request" }}
					</ion-button>
				</ion-toolbar>
			</ion-footer>
		</ion-modal>
	</RequirePermission>
</template>

<script setup lang="ts">
	import SgSelectModal from "@/components/ui/modals/SgSelectModal.vue";
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonButtons,
		IonButton,
		IonIcon,
		IonTitle,
		IonFooter,
	} from "@ionic/vue";
	import { close } from "ionicons/icons";
	import {
		TaskType,
		TaskTypeConfigs,
	} from "seedgreen-shared/dtos/TaskTypeConfig";
	import { computed, ref } from "vue";
	import { useI18n } from "vue-i18n";
	import { useRoute } from "vue-router";
	import TaskTypeChip from "@/components/feature/tasks/TaskTypeChip.vue";
	import DateModal from "@/components/ui/modals/DateModal.vue";
	import SgInput from "@/components/ui/SgInput.vue";
	import SgTextarea from "@/components/ui/SgTextarea.vue";
	import SgDatetimeButton from "@/components/ui/SgDatetimeButton.vue";
	import PlantingSelectorTrigger from "@/components/feature/notes/PlantingSelectorTrigger.vue";
	import { useSaveTaskRequest } from "seedgreen-shared/queries/task-request";
	import { sendToast } from "@/utils/sendToast";
	import {
		SaveTaskRequestDTO,
		TaskRequestStatus,
	} from "seedgreen-shared/dtos/TaskRequestDTO";
	import {
		DateString,
		getCurrentDateString,
	} from "seedgreen-shared/types/datestring.types";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import { useMyCustomer } from "seedgreen-shared/queries/customer";
	import RequirePermission from "@/components/feature/auth/RequirePermission.vue";
	import { PermissionKey } from "seedgreen-shared/dtos/PermissionTypeDTO";
	import { useModalNavigation } from "@/composables/useModalNavigation";
	import PlantMethodConflictWarning from "@/components/feature/tasks/TaskRequestModal/PlantMethodConflictWarning.vue";
	import BonipakTaskRequestWeedFieldForm from "@/components/feature/tasks/TaskRequestModal/BonipakTaskRequestWeedFieldForm.vue";
	import BonipakTaskRequestThinFieldForm from "@/components/feature/tasks/TaskRequestModal/BonipakTaskRequestThinFieldForm.vue";
	import type {
		BonipakWeedFieldData,
		BonipakThinFieldData,
	} from "seedgreen-shared/dtos/TaskRequestDTO";

	const route = useRoute();
	const { closeGlobalModal } = useModalNavigation();

	const { t } = useI18n();

	const modal = ref<InstanceType<typeof IonModal>>();
	const showModal = computed(() => {
		return route.query.modal === "task-request";
	});
	function dismiss() {
		modal.value?.$el.dismiss();
	}

	/** Form Data */
	const selectedPlantingIds = ref<number[]>([]);
	const selectedType = ref<TaskType>(TaskType.General);
	const selectedDate = ref<DateString>(getCurrentDateString());
	const taskTitle = ref<string>("");
	const requestComments = ref<string>("");

	// Pre-select planting if plantingId is in the URL when modal opens, otherwise clear
	function onWillPresent() {
		if (route.query.plantingId) {
			const plantingId = Number(route.query.plantingId);
			if (!isNaN(plantingId)) {
				selectedPlantingIds.value = [plantingId];
			} else {
				selectedPlantingIds.value = [];
			}
		} else {
			selectedPlantingIds.value = [];
		}
	}

	const bonipakFormData = ref<BonipakWeedFieldData>();
	const bonipakThinFormData = ref<BonipakThinFieldData>();
	function resetForm() {
		selectedPlantingIds.value = [];
		selectedDate.value = getCurrentDateString();
		taskTitle.value = "";
		requestComments.value = "";
		bonipakFormData.value = undefined;
		bonipakThinFormData.value = undefined;
		// Not resetting selectedType here for convenience
	}

	const canSubmit = computed(
		() =>
			selectedPlantingIds.value.length > 0 &&
			// General tasks must have a title
			(selectedType.value !== TaskType.General ||
				taskTitle.value.trim() !== ""),
	);

	const taskTypeItems = Object.entries(TaskTypeConfigs).map(
		([key, config]) => ({
			id: Number(key) as TaskType,
			text: t(config.name),
			color: config.color,
			textColor: config.contrastColor,
			badge: true,
		}),
	);

	const { saveTaskRequest } = useSaveTaskRequest();
	const { myUser } = useMyUser();
	const { myCustomer } = useMyCustomer();

	const isSubmitting = ref(false);
	async function submit() {
		if (!canSubmit.value || !myUser.value) return;

		isSubmitting.value = true;

		try {
			for (const plantingId of selectedPlantingIds.value) {
				const dto: SaveTaskRequestDTO = {
					plantingId,
					customerId: myUser.value.customerId ?? 0,
					requestType: selectedType.value,
					requestStatus: TaskRequestStatus.Pending,
					requestDate: selectedDate.value,
					title:
						selectedType.value === TaskType.General
							? taskTitle.value
							: undefined,
					requestComments: requestComments.value,
					requestingUserId: myUser.value.id,
					customData:
						bonipakFormData.value || bonipakThinFormData.value
							? {
									bonipakWeedFieldData: bonipakFormData.value,
									bonipakThinFieldData:
										bonipakThinFormData.value,
								}
							: undefined,
				};
				await saveTaskRequest(dto);
			}

			dismiss();
			resetForm();

			sendToast("Task request submitted", 1000, "primary");
		} catch (error) {
			sendToast("Error submitting task request", 2000, "danger");
			throw error;
		} finally {
			isSubmitting.value = false;
		}
	}
</script>
